import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../utils/api';
import * as storage from '../utils/storage';
import useAuthStore from '../stores/useAuthStore';

const KEY = ['tasks'];

function normalize(t) {
  return { ...t, id: t.taskId || t.id, tags: t.tags || [], checklist: t.checklist || [] };
}

export function useTasks() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: KEY,
    enabled: isAuthenticated,
    queryFn: async () => {
      const tasks = await api.fetchAllTasks();
      const normalized = tasks.map(normalize);
      storage.saveTasks(normalized);
      return normalized;
    },
    initialData: () => {
      const local = storage.loadTasks?.() || [];
      return local.length ? local.map(normalize) : undefined;
    },
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (task) => api.createTask(task),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (task) => api.updateTask(task),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId) => api.completeTask(taskId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUncompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId) => api.uncompleteTask(taskId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId) => api.deleteTask(taskId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, projectId }) => api.moveTask(id, status, projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReorderTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tasks) => api.reorderTasks(tasks.map((t) => ({ taskId: t.id, position: t.position }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
